/**
 * Delegate policies (ADR 0074) for the project's Autonomy dialog: the form model, what the policy
 * says in one line, and the live and demo data access. The form is pure, so it is unit tested.
 */
import { useEffect, useState } from 'react';
import { DELEGATE_DAYS_DEFAULT, DELEGATE_PER_MINUTE_DEFAULT, DELEGATES_PATH, delegateActive, delegateInputError, delegatePolicyPath } from '@orgus/protocol';
import type { ApprovalsState, DelegateAgent, DelegateClass, DelegatePolicy, DelegatePolicyInput, DelegatesResponse, Id } from '@orgus/protocol';
import { ApiError, api, apiDelete, apiGet, toUiError } from '../../lib/api.ts';
import type { UiError } from '../../lib/api.ts';
import { useOrgus } from '../../lib/store.ts';
import { MOCK_DELEGATE_AGENTS } from './mock.ts';

const DAY_MS = 24 * 60 * 60_000;

export const PER_MINUTE_OPTIONS = [5, 10, 20, 40, 60] as const;
export const DAY_OPTIONS = [1, 3, 7, 14, 30] as const;

export interface DelegateForm {
  agent: string;
  classes: DelegateClass[];
  perMinute: number;
  days: number;
}

/** 'on' answers now; 'expired' and 'revoked' answer nothing; 'none': the project has no policy. */
export type DelegateStatus = 'on' | 'expired' | 'revoked' | 'none';

export function delegateStatus(p: DelegatePolicy | undefined, now: number): DelegateStatus {
  if (!p) return 'none';
  if (p.revokedAt) return 'revoked';
  return delegateActive(p, now) ? 'on' : 'expired';
}

export function policyOf(state: Pick<ApprovalsState, 'delegates'> | null | undefined, projectId: Id): DelegatePolicy | undefined {
  return state?.delegates?.find((p) => p.projectId === projectId);
}

/** The form a policy starts from: its own values while it answers, else the defaults (the agent kept). */
export function formOf(p: DelegatePolicy | undefined, now: number, agents: readonly DelegateAgent[]): DelegateForm {
  const firstOk = agents.find((a) => !a.problem)?.name ?? '';
  if (!p) return { agent: firstOk, classes: ['edits', 'tests', 'typecheck', 'checks', 'git'], perMinute: DELEGATE_PER_MINUTE_DEFAULT, days: DELEGATE_DAYS_DEFAULT };
  const left = Math.ceil((p.expiresAt - now) / DAY_MS);
  const days = delegateStatus(p, now) === 'on' ? (DAY_OPTIONS.find((d) => d >= left) ?? DELEGATE_DAYS_DEFAULT) : DELEGATE_DAYS_DEFAULT;
  return { agent: p.agent, classes: [...p.classes], perMinute: p.perMinute, days };
}

export function toggleClass(f: DelegateForm, c: DelegateClass, on: boolean): DelegateForm {
  const classes = on ? [...new Set([...f.classes, c])] : f.classes.filter((x) => x !== c);
  return { ...f, classes };
}

/** The body to save, or the problem with it in one sentence (the hub's own check). */
export function inputOf(f: DelegateForm): { ok: true; input: DelegatePolicyInput } | { ok: false; error: string } {
  if (!f.agent) return { ok: false, error: 'Pick the agent that answers.' };
  const input: DelegatePolicyInput = { agent: f.agent, classes: f.classes, perMinute: f.perMinute, days: f.days };
  const err = delegateInputError(input);
  return err ? { ok: false, error: err } : { ok: true, input };
}

/** The saved policy differs from the form (Save is offered only then, or when nothing answers). */
export function changed(p: DelegatePolicy | undefined, f: DelegateForm, now: number): boolean {
  if (delegateStatus(p, now) !== 'on' || !p) return true;
  const same = p.agent === f.agent && p.perMinute === f.perMinute && p.classes.length === f.classes.length && p.classes.every((c) => f.classes.includes(c));
  return !same || formOf(p, now, []).days !== f.days;
}

// ---------------------------------------------------------------------------
// Data
// ---------------------------------------------------------------------------

export interface DelegateAgentsView {
  phase: 'loading' | 'error' | 'ready';
  agents: DelegateAgent[];
  error: UiError | null;
  retry: () => void;
}

/** The agent tokens a policy can name (admins see them; others get an empty list). */
export function useDelegateAgents(): DelegateAgentsView {
  const mock = useOrgus((s) => s.mode === 'mock');
  const [agents, setAgents] = useState<DelegateAgent[] | null>(null);
  const [error, setError] = useState<UiError | null>(null);
  const [tries, setTries] = useState(0);
  useEffect(() => {
    if (mock) return;
    const ctrl = new AbortController();
    setError(null);
    apiGet<DelegatesResponse>(DELEGATES_PATH, ctrl.signal).then(
      (r) => setAgents(r.agents),
      (e) => {
        if (ctrl.signal.aborted) return;
        if (e instanceof ApiError && e.status === 404) setAgents([]);
        else setError(toUiError(e));
      },
    );
    return () => ctrl.abort();
  }, [mock, tries]);
  const retry = () => setTries((n) => n + 1);
  if (mock) return { phase: 'ready', agents: MOCK_DELEGATE_AGENTS, error: null, retry };
  if (error) return { phase: 'error', agents: [], error, retry };
  return agents ? { phase: 'ready', agents, error: null, retry } : { phase: 'loading', agents: [], error: null, retry };
}

/** Replace this project's policy in state.ext.approvals (the hub broadcasts it too). */
function applyPolicy(policy: DelegatePolicy): void {
  const st = useOrgus.getState();
  const cur = st.ext.approvals as ApprovalsState | undefined;
  if (!cur) return;
  const delegates = [...(cur.delegates ?? []).filter((p) => p.projectId !== policy.projectId), policy];
  st.applyExt({ ext: 'approvals', type: 'state', data: { ...cur, delegates } satisfies ApprovalsState });
}

export async function saveDelegate(projectId: Id, input: DelegatePolicyInput): Promise<DelegatePolicy> {
  const st = useOrgus.getState();
  if (st.mode === 'mock') {
    await new Promise((r) => setTimeout(r, 300));
    const now = Date.now();
    const prev = policyOf(st.ext.approvals as ApprovalsState | undefined, projectId);
    const policy: DelegatePolicy = {
      id: prev?.id ?? `dlg_DEMO${now.toString(36).toUpperCase()}`,
      projectId,
      agent: input.agent,
      classes: input.classes,
      perMinute: input.perMinute ?? DELEGATE_PER_MINUTE_DEFAULT,
      expiresAt: now + (input.days ?? DELEGATE_DAYS_DEFAULT) * DAY_MS,
      version: (prev?.version ?? 0) + 1,
      createdBy: prev?.createdBy ?? 'you',
      createdAt: prev?.createdAt ?? now,
      updatedBy: 'you',
      updatedAt: now,
      ...(prev?.answered ? { answered: prev.answered, ...(prev.lastAnsweredAt ? { lastAnsweredAt: prev.lastAnsweredAt } : {}) } : {}),
    };
    applyPolicy(policy);
    return policy;
  }
  const { policy } = await api<{ policy: DelegatePolicy }>(delegatePolicyPath(projectId), { method: 'PUT', body: input });
  applyPolicy(policy);
  return policy;
}

export async function revokeDelegate(projectId: Id): Promise<DelegatePolicy> {
  const st = useOrgus.getState();
  if (st.mode === 'mock') {
    await new Promise((r) => setTimeout(r, 300));
    const prev = policyOf(st.ext.approvals as ApprovalsState | undefined, projectId);
    if (!prev) throw new ApiError({ what: 'The delegate policy was not revoked.', why: 'This project has none.', next: 'Reload the page.' }, 404);
    const policy: DelegatePolicy = { ...prev, revokedAt: Date.now(), revokedBy: 'you' };
    applyPolicy(policy);
    return policy;
  }
  const { policy } = await apiDelete<{ policy: DelegatePolicy }>(delegatePolicyPath(projectId));
  applyPolicy(policy);
  return policy;
}
